/* eslint-disable no-param-reassign */
import React, { useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Axios from 'axios';
import { CloudUpload } from '@material-ui/icons';
import config from '../../../config';

import {
  Content,
  ContainerCard,
  InputFileContainer,
  Input,
  ErrorLabel,
  ErrorList,
} from '../styledComponents';

const numberRegex = /^\d+$/;
const ADDRESS_PROOF_VALIDATION_URL = `${config.api.url}/documentosClientes/validar-comprobante-domicilio`;

const DomicilioActual = ({
  clientInfo,
  editAddress,
  editAddressStatus,
  existAddress,
  newPostalCode,
  setNewPostalCode,
  newCity,
  setNewCity,
  newState,
  setNewState,
  newSuburb,
  setNewSuburb,
  suburbsOptions,
  newStreet,
  setNewStreet,
  newNumber,
  setNewNumber,
  addressProofFile,
  addressProofFileName,
  setAddressProofFile,
  setAddressProofFileName,
  requiereComprobanteDom,
  plazaRequiereComprobanteDom,
  maxValidationAttempts,
  token,
  addressFromProof,
  setAddressFromProof,
  setLoading,
  documentoClienteId,
}) => {
  const inputFileRef = useRef(null);
  const [localAddressProofFile, setLocalAddressProofFile] = useState('');
  const [localAddressProofFileName, setLocalAddressProofFileName] = useState(
    '',
  );

  const [uploadAttempts, setUploadAttempts] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const currentAddressProofFile = addressProofFile || localAddressProofFile;
  const currentAddressProofFileName =
    addressProofFileName || localAddressProofFileName;

  const parsedMaxAttempts = Number(maxValidationAttempts);
  const maxAttemptsLimit =
    Number.isFinite(parsedMaxAttempts) && parsedMaxAttempts > 0
      ? parsedMaxAttempts
      : Infinity;
  const maxAttemptsReached = uploadAttempts >= maxAttemptsLimit;

  const isImageFile =
    !!currentAddressProofFileName &&
    /\.(jpe?g|png)$/i.test(currentAddressProofFileName);

  const validateAddressProof = async selectedFile => {
    const formData = new FormData();
    formData.append('comprobanteDomicilio', selectedFile);
    formData.append('token', token);

    const response = await Axios.post(ADDRESS_PROOF_VALIDATION_URL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const domicilio = get(response, 'data.comprobanteDomicilio', true);

    const domicilioComprobante = get(
      response,
      'data.comprobanteDomicilio.domicilioComprobante',
      true,
    );

    documentoClienteId.current = get(response, 'data.documentoClienteId', null);

    return { domicilio, domicilioComprobante };
  };

  const handleFileChange = async event => {
    const selectedFile = get(event, 'target.files[0]', null);
    if (!selectedFile) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type)) {
      setUploadError('Formato no permitido. Usa PDF, JPG o PNG.');
      event.target.value = '';
      return;
    }

    if (maxAttemptsReached || uploading) {
      event.target.value = '';
      return;
    }

    setUploading(true);
    setUploadError('');

    setLoading(true);
    try {
      const isValidDocument = await validateAddressProof(selectedFile);

      if (!isValidDocument) {
        setUploadAttempts(previousAttempts => {
          const nextAttempts = previousAttempts + 1;
          if (nextAttempts >= maxAttemptsLimit) {
            editAddressStatus(true);
          }
          return nextAttempts;
        });
        setUploadError(
          'No pudimos validar tu comprobante. Verifica el documento e intenta de nuevo.',
        );
        setLocalAddressProofFile('');
        setLocalAddressProofFileName('');

        if (setAddressProofFile) {
          setAddressProofFile('');
        }

        if (setAddressProofFileName) {
          setAddressProofFileName('');
        }

        event.target.value = '';
        return;
      }

      const { calle, colonia, cp, numeroExterior } = isValidDocument.domicilio;

      const { ciudad, estado } = isValidDocument.domicilioComprobante;

      setAddressFromProof(true);

      setNewCity(ciudad);
      setNewPostalCode(cp);
      setNewStreet(calle);
      setNewSuburb(colonia);
      setNewState(estado);
      setNewNumber(numeroExterior);

      const reader = new FileReader();
      reader.onload = readerEvent => {
        const fileBase64 = get(readerEvent, 'target.result', '');
        const fileName = selectedFile.name || '';

        setLocalAddressProofFile(fileBase64);
        setLocalAddressProofFileName(fileName);

        if (setAddressProofFile) {
          setAddressProofFile(fileBase64);
        }

        if (setAddressProofFileName) {
          setAddressProofFileName(fileName);
        }

        event.target.value = '';
      };

      reader.readAsDataURL(selectedFile);
    } catch (error) {
      setUploadAttempts(previousAttempts => {
        const nextAttempts = previousAttempts + 1;
        if (nextAttempts >= maxAttemptsLimit) {
          editAddressStatus(true);
        }
        return nextAttempts;
      });
      // console.log({ errorBackend: error.response.data.error.message });

      setUploadError(
        get(
          error,
          'response.data.error.message',
          'No pudimos validar tu comprobante. Verifica el documento e intenta de nuevo.',
        ),
      );
      setLocalAddressProofFile('');
      setLocalAddressProofFileName('');

      setNewCity('');
      setNewPostalCode('');
      setNewStreet('');
      setNewSuburb('');
      setNewState('');
      setNewNumber('');

      if (setAddressProofFile) {
        setAddressProofFile('');
      }

      if (setAddressProofFileName) {
        setAddressProofFileName('');
      }
      event.target.value = '';
    } finally {
      setUploading(false);
      setLoading(false);
    }
  };

  return (
    <Content>
      <h1>Domicilio actual</h1>
      {plazaRequiereComprobanteDom && (
        <h2
          style={{
            width: '100%',
            fontSize: 18,
            color: '#424242',
            marginBottom: '40px',
          }}
        >
          Por favor carga tu comprobante de domicilio el cual no debe tener una
          una antigüedad mayor a tres meses; (CFE, Telmex, Telcel, Izzi,
          Totalplay, Megacable)
        </h2>
      )}

      {!editAddress ? (
        (!requiereComprobanteDom || currentAddressProofFile) &&
        !uploadError && (
          <div>
            <ContainerCard>
              <div className="greeting">Mi domicilio es el siguiente: </div>
              {!plazaRequiereComprobanteDom && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => editAddressStatus(true)}
                >
                  Editar dirección
                </Button>
              )}
            </ContainerCard>
            <div>
              {`${newStreet} `}
              {`${newNumber} `}
              {`Col. ${newSuburb} `}
              {`C.P. ${newPostalCode} `}
              {`${newCity}, `}
              {`${newState} `}
            </div>
          </div>
        )
      ) : (
        <Content>
          <ContainerCard>
            <div className="greeting">Dirección</div>
            {existAddress && !plazaRequiereComprobanteDom && (
              <Button
                variant="text"
                color="primary"
                onClick={() => editAddressStatus(false)}
              >
                Cancelar
              </Button>
            )}
          </ContainerCard>
          <h2 style={{ width: '100%' }}>Código postal</h2>
          <TextField
            id="postal-code"
            value={newPostalCode}
            variant="outlined"
            onChange={e => {
              const { value } = e.target;
              if (!value || numberRegex.test(value)) {
                setNewPostalCode(value);
              }
            }}
            fullWidth
            inputProps={{ maxLength: 5 }}
          />

          <h2 style={{ width: '100%' }}>Ciudad</h2>
          <TextField
            id="city"
            value={newCity}
            onChange={e => setNewCity(e.target.value)}
            variant="outlined"
            fullWidth
          />
          <h2 style={{ width: '100%' }}>Estado</h2>
          <TextField
            id="state"
            value={newState}
            onChange={e => setNewState(e.target.value)}
            variant="outlined"
            fullWidth
          />

          <h2 style={{ width: '100%' }}>Colonia</h2>
          <FormControl variant="outlined" fullWidth>
            <Select
              labelId="suburb-label"
              id="suburb"
              value={newSuburb}
              onChange={event => setNewSuburb(event.target.value)}
            >
              {suburbsOptions.map(c => (
                <MenuItem value={c.nomColonia} key={c.id}>
                  {c.nomColonia}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <h2 style={{ width: '100%' }}>Calle</h2>
          <TextField
            id="street"
            value={newStreet}
            variant="outlined"
            onChange={e => setNewStreet(e.target.value)}
            fullWidth
          />

          <h2 style={{ width: '100%' }}>Número exterior</h2>
          <TextField
            id="number"
            value={newNumber}
            variant="outlined"
            onChange={e => {
              const { value } = e.target;
              if (!value || numberRegex.test(value)) {
                setNewNumber(value);
              }
            }}
            fullWidth
            inputProps={{ maxLength: 5 }}
          />
        </Content>
      )}

      {plazaRequiereComprobanteDom && !editAddress && (
        <>
          <h2 style={{ width: '100%' }}>Comprobante de domicilio</h2>
          <Input
            ref={inputFileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            disabled={maxAttemptsReached || uploading}
          />
          <InputFileContainer
            onClick={() => {
              if (maxAttemptsReached || uploading) {
                return;
              }
              if (inputFileRef.current) {
                inputFileRef.current.click();
              }
            }}
            style={{
              opacity: maxAttemptsReached || uploading ? 0.6 : 1,
              cursor:
                maxAttemptsReached || uploading ? 'not-allowed' : 'pointer',
            }}
          >
            {!currentAddressProofFile ? (
              <>
                <CloudUpload style={{ fontSize: 24 }} />
                {!requiereComprobanteDom ? (
                  <span>Documento Cargado</span>
                ) : (
                  <span>{uploading ? 'Validando...' : 'Subir'}</span>
                )}
              </>
            ) : (
              <>
                {isImageFile ? (
                  <img
                    alt={currentAddressProofFileName || 'comprobante-domicilio'}
                    src={currentAddressProofFile}
                  />
                ) : (
                  <>
                    <CloudUpload style={{ fontSize: 24 }} />
                    <span>
                      {currentAddressProofFileName || 'Documento cargado'}
                    </span>
                  </>
                )}
              </>
            )}
          </InputFileContainer>
          {(uploadError || maxAttemptsReached) && (
            <>
              <ErrorLabel style={{ width: '100%' }}>
                {maxAttemptsReached
                  ? 'Alcanzaste el maximo de intentos. Por favor captura tu domicilio manualmente.'
                  : uploadError}
              </ErrorLabel>
              <ErrorLabel style={{ width: '100%', marginTop: '20px' }}>
                No logramos validar tu comprobante, intenta de nuevo lo
                siguiente:
              </ErrorLabel>
              <ErrorList>
                <li>
                  Comprobante valido (CFE, Telmex, Telcel, Izzi, Totalplay,
                  Megacable)
                </li>
                <li>Antigüedad menor a tres meses</li>
                <li>Claridad del documento</li>
                <li>Claridad de la imagen</li>
              </ErrorList>
            </>
          )}
        </>
      )}
    </Content>
  );
};

DomicilioActual.propTypes = {
  clientInfo: PropTypes.object,
  editAddress: PropTypes.bool.isRequired,
  editAddressStatus: PropTypes.func.isRequired,
  existAddress: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  newPostalCode: PropTypes.string.isRequired,
  setNewPostalCode: PropTypes.func.isRequired,
  newCity: PropTypes.string.isRequired,
  setNewCity: PropTypes.func.isRequired,
  newState: PropTypes.string.isRequired,
  setNewState: PropTypes.func.isRequired,
  newSuburb: PropTypes.string.isRequired,
  setNewSuburb: PropTypes.func.isRequired,
  suburbsOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      nomColonia: PropTypes.string.isRequired,
    }),
  ).isRequired,
  newStreet: PropTypes.string.isRequired,
  setNewStreet: PropTypes.func.isRequired,
  newNumber: PropTypes.string.isRequired,
  setNewNumber: PropTypes.func.isRequired,
  addressProofFile: PropTypes.string,
  addressProofFileName: PropTypes.string,
  setAddressProofFile: PropTypes.func,
  setAddressProofFileName: PropTypes.func,
  requiereComprobanteDom: PropTypes.oneOfType([PropTypes.bool]),
  plazaRequiereComprobanteDom: PropTypes.oneOfType([PropTypes.bool]),
  maxValidationAttempts: PropTypes.oneOfType([PropTypes.number]),
  token: PropTypes.string,
  addressFromProof: PropTypes.bool.isRequired,
  setAddressFromProof: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  documentoClienteId: PropTypes.shape({ current: PropTypes.any }),
};

DomicilioActual.defaultProps = {
  clientInfo: null,
  existAddress: false,
  addressProofFile: '',
  addressProofFileName: '',
  setAddressProofFile: null,
  setAddressProofFileName: null,
  requiereComprobanteDom: false,
  plazaRequiereComprobanteDom: false,
  maxValidationAttempts: '',
  documentoClienteId: { current: null },
};

export default DomicilioActual;
